/* Shared navigation for homepage/PDP galleries. Existing components retain their
   content, animation and autoplay ownership; this layer owns navigation UI. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  // One thumb treatment for every scroll-linked bar (Remy 2026-09-25, "it
  // starts vibrating back and forth"): it tracks the rail 1:1 (no CSS
  // transition), moves by transform only, and never wraps. Past either end (the
  // iOS rubber band, or a loop's forward sentinel) it holds that end and
  // squeezes against it instead of jumping across. home-formulary-carousel.js
  // carries the same function; keep the two identical.
  function paintThumb(thumb, position, count) {
    const visible = Math.max(1 / Math.max(1, count), 0.12);
    const last = Math.max(1, count - 1);
    const over = position < 0 ? -position : position > last ? position - last : 0;
    const fraction = Math.min(1, Math.max(0, position / last));
    const width = (visible * 100).toFixed(2) + '%';
    if (thumb.style.width !== width) thumb.style.width = width;
    thumb.style.transformOrigin = position > last ? 'right center' : 'left center';
    thumb.style.transform = 'translateX(' + (fraction * (1 - visible) / visible * 100).toFixed(2) + '%) scaleX(' + (1 - Math.min(0.5, over * 0.5)).toFixed(3) + ')';
  }
  const definitions = [
    ['creatine-hero-gallery', '.cm-gallery__track', '.cm-gallery__slide', '.cm-gallery__arrow'],
    ['featured-review-gallery', '[data-review-track]', '[data-review-slide]', '[data-review-controls]', 'reviews'],
    ['anti-medical-chapter', '.anti-medical__rail', '.anti-medical__track > *', '.anti-medical__controls', 'advisors'],
    ['anti-trust-carousel', '.anti-trust__track', '.anti-trust__card', '.anti-trust__footer'],
    ['anti-experience-gallery', '.anti-experience__track', '.anti-experience__card', '.anti-experience__footer'],
    ['.advisor-quote-cards', '.advisor-quote-cards__rail', '.advisor-quote-cards__card', '.advisor-quote-cards__controls'],
    ['.home-formulary', '.card-rail', '.ingredient', '.gallery-dots'],
    ['wp-recipes', '.wp-r-rail', '.wp-r-card', '.wp-r-rail-footer'],
    ['.wp-r-detail-panels', '.wp-r-panel-track', '.wp-r-panel', '.wp-r-panel-head, .wp-r-panel-dots'],
    ['anti-sourcing-story', '.anti-sourcing__track', '.anti-sourcing__slide', ''],
    ['.cc-home-grid', '.cc-home-grid__rail', '.cc-home-grid__rail > *', ''],
    ['.antilabs-review-photos', '.antilabs-review-photos__strip', '.antilabs-review-photos__thumbnail', ''],
    ['.antilabs-review-photos', '.antilabs-review-photos__detail', '.antilabs-review-photos__thumbnail', '.antilabs-review-photos__actions, [data-photo-position]', 'photos']
  ];
  let serial = 0;
  const instances = new Map();
  const pending = new WeakSet();
  function enhance(root, definition) {
    if (root.hasAttribute('data-formulary-loop') || root.hasAttribute('data-advisor-loop')) return;
    if (root.localName.includes('-') && !customElements.get(root.localName)) {
      if (!pending.has(root)) {
        pending.add(root);
        customElements.whenDefined(root.localName).then(() => { pending.delete(root); if (root.isConnected) enhance(root, definition); });
      }
      return;
    }
    const [, railSelector, cardSelector, legacySelector, mode] = definition;
    const rail = root.querySelector(railSelector);
    if (!rail || instances.has(rail)) return;
    let cards = Array.from(root.querySelectorAll(cardSelector));
    if (cards.length < 2) return;
    const hero = root.matches('creatine-hero-gallery');
    const formulary = root.matches('.home-formulary');
    const navigation = root.matches('.antilabs-review-photos') ? (mode === 'photos' ? 'dots' : 'none') : (hero || mode === 'reviews' || formulary) ? 'bar' : 'none';
    const originalDots = Array.from(root.querySelectorAll('[data-review-dot]'));
    const advisorCards = mode === 'advisors' ? Array.from(rail.firstElementChild.children) : [];
    const timed = mode === 'reviews' || mode === 'advisors';
    const photoDialog = mode === 'photos' ? root.querySelector('dialog') : null;
    const controlled = timed || !!photoDialog;
    const controller = new AbortController(), signal = controller.signal;
    const nav = document.createElement('div');
    nav.className = 'cc-carousel-nav';
    nav.dataset.navigation = navigation;
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', (rail.getAttribute('aria-label') || root.getAttribute('aria-label') || 'Gallery') + ' navigation');
    const dots = document.createElement('div');
    dots.className = 'cc-carousel-dots';
    nav.append(dots);
    const progress = document.createElement('div');
    const thumb = document.createElement('span');
    if (navigation === 'bar') {
      progress.className = 'cc-carousel-progress';
      progress.setAttribute('aria-hidden', 'true');
      progress.append(thumb);
      nav.append(progress);
      nav.setAttribute('aria-hidden', 'true');
    }
    (formulary ? root.querySelector('.home-formulary__surface') || rail : rail).insertAdjacentElement('afterend', nav);
    if (!rail.id) rail.id = 'storefront-gallery-' + (++serial);
    if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
    rail.setAttribute('aria-roledescription', 'carousel');
    if (!rail.hasAttribute('role')) rail.setAttribute('role', 'region');
    if (!rail.hasAttribute('aria-label')) rail.setAttribute('aria-label', 'Gallery');
    if (legacySelector) root.querySelectorAll(legacySelector).forEach(el => el.classList.add('cc-carousel-legacy'));
    let positions = [], buttons = [], active = 0, scheduled = 0;
    const measure = () => {
      cards = Array.from(root.querySelectorAll(cardSelector)).filter(card => !card.hasAttribute('data-gallery-clone'));
      if (hero) positions = cards.map((_, i) => i);
      else if (photoDialog) positions = Array.from({length: Number(photoDialog.dataset.carouselCount) || cards.length}, (_, i) => i);
      else if (timed) positions = cards.map((_, i) => i);
      else {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const left = rail.getBoundingClientRect().left + rail.clientLeft;
        const railStyle = getComputedStyle(rail);
        const inset = parseFloat(railStyle.scrollPaddingLeft) || parseFloat(railStyle.paddingLeft) || 0;
        positions = cards.map(card => Math.max(0, Math.min(max, card.getBoundingClientRect().left - left + rail.scrollLeft - inset)));
        positions = positions.filter((value, i, all) => i === 0 || Math.abs(value - all[i - 1]) > 2);
      }
      nav.hidden = navigation === 'none' || positions.length < 2 || (!controlled && rail.scrollWidth <= rail.clientWidth + 2);
      if (navigation === 'dots' && buttons.length !== positions.length) {
        dots.replaceChildren();
        buttons = positions.map((_, i) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'cc-carousel-dot';
          button.setAttribute('aria-label', `Show slide ${i + 1} of ${positions.length}`);
          button.setAttribute('aria-controls', rail.id);
          button.addEventListener('click', () => select(i), { signal });
          dots.append(button); return button;
        });
      }
      sync();
    };
    function sync() {
      if (hero) active = root.index || 0;
      else if (photoDialog) active = Number(photoDialog.dataset.carouselIndex) || 0;
      else if (mode === 'reviews') active = Math.max(0, originalDots.findIndex(el => el.getAttribute('aria-current') === 'true'));
      else if (mode === 'advisors') active = Math.max(0, advisorCards.findIndex(el => el.getAttribute('aria-pressed') === 'true'));
      else active = positions.reduce((best, p, i) => Math.abs(p - rail.scrollLeft) < Math.abs(positions[best] - rail.scrollLeft) ? i : best, 0);
      paintProgress();
      const start = Math.max(0, Math.min(active - 2, buttons.length - 5));
      buttons.forEach((button, i) => {
        button.setAttribute('aria-current', String(i === active));
        button.tabIndex = i === active ? 0 : -1;
        button.hidden = i < start || i >= start + 5;
        button.dataset.edge = String((i === start && start > 0) || (i === start + 4 && i < buttons.length - 1));
      });
    }
    function paintProgress() {
      if (navigation !== 'bar') return;
      const count = positions.length;
      // Match the live PDP: visible fraction, 12% floor, and continuous travel
      // in multiples of the thumb's own width. Loop sentinels are not slides.
      let position = active;
      if (formulary && rail.scrollWidth > rail.clientWidth) position = rail.scrollLeft / (rail.scrollWidth - rail.clientWidth) * Math.max(1, count - 1);
      if (hero && rail.clientWidth) position = rail.scrollLeft / rail.clientWidth;
      else if (mode === 'reviews') {
        const viewport = root.querySelector('.cc-featured-reviews__viewport');
        if (viewport?.clientWidth) position = (viewport.getBoundingClientRect().left - cards[0].getBoundingClientRect().left) / viewport.clientWidth;
      }
      paintThumb(thumb, position, count);
    }
    // Review slides move with a CSS transform rather than native scrollLeft.
    // Sample their rendered position only while a drag or transition is active.
    let motionFrame = 0;
    function followReviewMotion() {
      cancelAnimationFrame(motionFrame);
      const paint = () => {
        paintProgress();
        motionFrame = mode === 'reviews' && (root.moving || root.dragging) ? requestAnimationFrame(paint) : 0;
      };
      motionFrame = requestAnimationFrame(paint);
    }
    if (mode === 'reviews') root.addEventListener('anti:review-motion', followReviewMotion, { signal });
    signal.addEventListener('abort', () => cancelAnimationFrame(motionFrame));
    function select(index) {
      const requested = index;
      index = hero ? Math.max(0, index) % positions.length : Math.max(0, Math.min(positions.length - 1, index));
      if (photoDialog) root.dispatchEvent(new CustomEvent('anti:photo-select', {detail: {index}}));
      else if (mode === 'reviews') originalDots[index]?.click();
      else if (mode === 'advisors') advisorCards[index]?.click();
      else if (root.matches('creatine-hero-gallery') && typeof root.go === 'function') root.go(requested);
      else rail.scrollTo({ left: positions[index], behavior: reduced.matches ? 'instant' : 'smooth' });
      // Reveal the requested dot immediately, then let scroll/selection settle.
      active = index;
      const start = Math.max(0, Math.min(index - 2, buttons.length - 5));
      buttons.forEach((b, i) => { b.hidden = i < start || i >= start + 5; });
    }
    function key(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (event.target.closest('input:not([type=range]),textarea,select,[role=tablist]')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? positions.length - 1 : active + (event.key === 'ArrowRight' ? 1 : -1);
      select(target);
      if (nav.contains(event.target)) buttons[Math.max(0, Math.min(buttons.length - 1, target))]?.focus({ preventScroll: true });
    }
    root.addEventListener('anti:gallery-change', sync, { signal });
    rail.addEventListener('keydown', key, { signal, capture: true });
    nav.addEventListener('keydown', key, { signal });
    rail.addEventListener('scroll', () => {
      cancelAnimationFrame(scheduled); scheduled = requestAnimationFrame(sync);
    }, { signal, passive: true });
    // Native touch/trackpad scrolling plus mouse drag on every scroll rail.
    // Hero and spotlight already own their specialized drag animation.
    if (!controlled && !root.matches('creatine-hero-gallery')) {
      let drag = null, suppressClick = false, suppressTimer;
      rail.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('input,select,textarea')) return;
        drag = { id: event.pointerId, x: event.clientX, left: rail.scrollLeft, moved: false };
      }, { signal });
      rail.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (!drag.moved && Math.abs(event.clientX - drag.x) < 6) return;
        drag.moved = true; rail.classList.add('cc-carousel-dragging');
        rail.setPointerCapture(event.pointerId); event.preventDefault();
        rail.scrollLeft = drag.left - (event.clientX - drag.x);
      }, { signal });
      const finishDrag = event => {
        if (!drag || event.pointerId !== drag.id) return;
        const moved = drag.moved; drag = null;
        rail.classList.remove('cc-carousel-dragging');
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        if (moved) {
          suppressClick = true; clearTimeout(suppressTimer);
          suppressTimer = setTimeout(() => { suppressClick = false; }, 300);
          sync(); select(active);
        }
      };
      rail.addEventListener('pointerup', finishDrag, { signal });
      rail.addEventListener('pointercancel', finishDrag, { signal });
      rail.addEventListener('dragstart', event => event.preventDefault(), { signal });
      rail.addEventListener('click', event => { if (suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); suppressClick = false; } }, { signal, capture: true });
      signal.addEventListener('abort', () => clearTimeout(suppressTimer));
    }
    const observer = new MutationObserver(sync);
    // Observe only the legacy state source, not our own aria-current writes.
    if (timed) (mode === 'reviews' ? originalDots : advisorCards).forEach(el => observer.observe(el, { attributes: true, attributeFilter: [mode === 'reviews' ? 'aria-current' : 'aria-pressed'] }));
    const contentObserver = new MutationObserver(measure);
    if (photoDialog) {
      contentObserver.observe(photoDialog, { attributes: true, attributeFilter: ['data-carousel-index', 'data-carousel-count'] });
      let start = null;
      rail.addEventListener('pointerdown', event => { start = { x: event.clientX, y: event.clientY }; }, { signal });
      rail.addEventListener('pointerup', event => {
        if (!start) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y; start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) select(active + (dx < 0 ? 1 : -1));
      }, { signal });
      rail.addEventListener('pointercancel', () => { start = null; }, { signal });
    } else if (!timed) contentObserver.observe(rail, { childList: true });
    const resize = new ResizeObserver(measure); resize.observe(rail);
    measure();
    instances.set(rail, () => { controller.abort(); observer.disconnect(); contentObserver.disconnect(); resize.disconnect(); cancelAnimationFrame(scheduled); nav.remove(); });
  }
  function scan() {
    instances.forEach((dispose, rail) => { if (!rail.isConnected) { dispose(); instances.delete(rail); } });
    definitions.forEach(def => document.querySelectorAll(def[0]).forEach(root => enhance(root, def)));
  }
  function start() {
    scan();
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && !node.matches('.cc-carousel-nav,.cc-carousel-dot')))) scan();
    });
    observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', scan);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
